import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PencilIcon, Play } from "lucide-react";
import { ActionNode, BotJourney, JourneyPresetState, makeScheduledConfig, Tab, TriggerNode } from "../types";
import ConvoAIPanel, { ConvoAIMessage } from "../../../../components/marketing/campaign/convoai-hub";
import { SidebarPanel } from "../../../../components/marketing/journey/side-panel";
import { Canvas } from "../../../../components/marketing/journey/canvas";
import PreviewBotPanel from "../../../../components/marketing/journey/preview-bot";
import { RcsConfigPanel } from "../../../../components/marketing/journey/rcs-config-panel";
import ScrollToTop from "../../../../routes/scroll-top";
import useJouneyReq from "../../../../components/hook/bot-req";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import useRequest from "../../../../components/hook/use-req";
import { TemplateModule } from "../../../marketing-pages/templates";
import useJouneyFormReq from "../../../../components/hook/bot-form-req";
import { actionNodesToRcsFlow, buildJourneyPayload, getDefaultConfigForActionType, hydrateJourneyToCanvas, makeActionNode, rcsFlowToActionNodes } from "../constant";
import { makeAppointmentReminderJourneyActions, makeBirthdayJourneyActions, makeFeedbackCollectionJourneyActions, makeLeadQualificationJourneyActions, makeTriggerNode } from "../connected-actions";
import useRcsBotReq from "../../../../components/hook/rcs-bot-req";
import { useTenantId } from "../../../../components/hook/use-tenant-id";

const STORAGE_KEY = "convoai_journey";

const BotBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const journeyPreset = (location.state as JourneyPresetState | null)?.journeyPreset;
  const [botName, setBotName] = useState("Add a bot name");
  const [editingName, setEditingName] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("Triggers");
  const [showConvoAI, setShowConvoAI] = useState(false);
  const [triggerNode, setTriggerNode] = useState<TriggerNode | null>(null);
  const [actionNodes, setActionNodes] = useState<ActionNode[]>([]);
  const [versionId, setVersionId] = useState<number | null>(null);
  const [stepId, setStepId] = useState<string | null>(null);
  const [templates, setTemplates] = React.useState<TemplateModule[]>([]);
  const [presetLoaded, setPresetLoaded] = useState(false);
  const token = localStorage.getItem("authToken");
  const currentDraftVersion = (() => {
    try {
      const stored = localStorage.getItem("convoai_journey");
      if (!stored) return null;

      const parsed = JSON.parse(stored);
      return parsed?.current_draft_version ?? null;
    } catch {
      return null;
    }
  })();
  const [journeyId, setJourneyId] = useState<string | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? (JSON.parse(saved) as { id?: string }).id ?? null : null;
    } catch { return null; }
  });

  const isCreated = Boolean(journeyId);

  const { makeRequest: createJourney, loading } = useJouneyReq(`/journeys`, "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: saveDraftJourney, loading: draftLoader } = useJouneyReq(
    journeyId ? `/journeys/${journeyId}/trigger` : `/journeys`,
    journeyId ? "PATCH" : "POST", {
    Authorization: `Bearer ${token}`,
  }
  );

  const { makeRequest: getJourneyById } = useJouneyReq(`/journeys/${id}`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  // POST a fresh draft version with the current canvas definition. Without
  // this, saveDraft only PATCHes trigger config — the actionNodes changes
  // (e.g. switching a Condition to WhatsApp + picking templates + waits)
  // never reach the backend and publish keeps promoting the same stale
  // definition. Joseph 2026-08-04: "still not saving the what conditions
  // i published".
  const { makeRequest: createVersion } = useJouneyReq(
    journeyId ? `/journeys/${journeyId}/versions` : "",
    "POST",
    { Authorization: `Bearer ${token}` }
  );
  // Autosave writes the canvas into the DRAFT version in place. Not
  // POST /versions — that mints a new version per call, which would leave
  // hundreds of rows per editing session and make the history useless.
  const { makeRequest: autosaveDraft } = useJouneyReq(
    journeyId ? `/journeys/${journeyId}/versions/draft` : "",
    "PUT",
    { Authorization: `Bearer ${token}` }
  );
  const [savedAt, setSavedAt] = useState<string>("");

  const { makeRequest: publishJourney, loading: publishLoader } = useJouneyReq(
    journeyId ? `/journeys/${journeyId}/publish` : "",
    "POST",
    {
      Authorization: `Bearer ${token}`,
    }
  );

  const { makeRequest: deleteTrigger } = useJouneyReq(
    journeyId ? `/journeys/${journeyId}/trigger` : "",
    "DELETE",
    {
      Authorization: `Bearer ${token}`,
    }
  );

  const { makeRequest: deleteAction } = useJouneyReq(
    journeyId && versionId ? `/journeys/${journeyId}/versions/${versionId}/steps/${stepId}` : "",
    "DELETE",
    {
      Authorization: `Bearer ${token}`,
    }
  );

  // Fires a single event via useJouneyReq — same pattern as every
  // other working API call. Used by handlePublish's auto-fire step
  // (backend is now safe against duplicate/immediate-fire bugs).
  const { makeRequest: postExternalEvent } = useJouneyReq(
    "/v1/events/external",
    "POST",
    { Authorization: `Bearer ${token}` },
  );

  const { makeRequest: getTemplates } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  // ── RCS trigger — separate backend (services/rcs_bot_service), only used
  // while triggerNode?.type === "RCS Trigger Node". See actionNodesToRcsFlow/
  // rcsFlowToActionNodes in ../constant for the payload translation.
  const isRcsTrigger = triggerNode?.type === "RCS Trigger Node";
  const [rcsBotId, setRcsBotId] = useState<string | null>(null);
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [rcsSaving, setRcsSaving] = useState(false);
  const [rcsPublishing, setRcsPublishing] = useState(false);
  const [rcsDeleting, setRcsDeleting] = useState(false);
  const rcsTenantId = useTenantId();
  const { makeRequest: getRcsBot } = useRcsBotReq("/rcs/bots", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: createRcsBot } = useRcsBotReq("/rcs/bots", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: updateRcsDraft } = useRcsBotReq("/rcs/bots", "PUT", { Authorization: `Bearer ${token}` });
  const { makeRequest: publishRcsBot } = useRcsBotReq("/rcs/bots", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: deleteRcsBot } = useRcsBotReq("/rcs/bots", "DELETE", { Authorization: `Bearer ${token}` });

  const mediaUploadRequest = useJouneyFormReq("/media/upload", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const broadcastExcelUploadRequest = useJouneyFormReq("/broadcast/excel", "POST", {
    Authorization: `Bearer ${token}`,
  });

  const { handleSubmit } = useForm();

  useEffect(() => {
    const fetchTemplates = async () => {
      const [response] = await getTemplates();

      let allTemplates: TemplateModule[] = [];

      if (response?.data && Array.isArray(response.data)) {
        allTemplates = response.data;
      } else if (Array.isArray(response)) {
        allTemplates = response;
      }

      // Keep ALL templates (WhatsApp + Email)
      setTemplates(allTemplates);
    };

    fetchTemplates();
  }, []);

useEffect(() => {
  if (id || !journeyPreset) return;

  const presetTrigger = makeTriggerNode(journeyPreset.triggerType ?? "On Message");
  if (!presetTrigger) return;

  // === PRIORITY: Check by TITLE first (most reliable) ===
  const isBirthdayPreset = journeyPreset.title === "Birthday Campaign";
  const isLeadQualificationPreset = journeyPreset.title === "Lead Qualification";
  const isAppointmentReminderPreset = journeyPreset.title === "Appointment Reminder";
  const isFeedbackCollectionPreset = journeyPreset.title === "Feedback Collection";

  // Override the default eventType per preset. makeTriggerNode("On Event")
  // hardcodes Appointment_Scheduled — fine for Appointment Reminder but
  // wrong for Feedback Collection (Joseph 06-30: "what is appointment
  // schedule doing their"). Set the right event type per preset.
  if (presetTrigger.type === "On Event") {
    if (isFeedbackCollectionPreset) {
      (presetTrigger as any).eventType = "Feedback_Collection_Start";
    } else if (isAppointmentReminderPreset) {
      (presetTrigger as any).eventType = "Appointment_Scheduled";
    }
  }

  setTriggerNode((current) => current ?? presetTrigger);

  setBotName((current) => {
    if (current !== "Add a bot name") return current;

    if (isBirthdayPreset) return "Birthday Wishes";
    if (isAppointmentReminderPreset) return "Appointment Reminder";
    if (isLeadQualificationPreset) return "Lead Qualification";
    if (isFeedbackCollectionPreset) return "Feedback Collection";

    return journeyPreset.title || "New Journey";
  });

  // Load full dedicated flows
  if (isBirthdayPreset) {
    setActionNodes((current) => current.length ? current : makeBirthdayJourneyActions());
  } 
  else if (isLeadQualificationPreset) {
    setActionNodes((current) => current.length ? current : makeLeadQualificationJourneyActions());
  } 
  else if (isAppointmentReminderPreset) {
    setActionNodes((current) => current.length ? current : makeAppointmentReminderJourneyActions());
  } 
else if (isFeedbackCollectionPreset) {
  setActionNodes((current) => current.length ? current : makeFeedbackCollectionJourneyActions());
}

  setActiveTab("Actions");
  setPresetLoaded(true);
}, [id, journeyPreset]);
  useEffect(() => {
    if (!templates.length) return;
    const birthdayTemplate = templates.find(
      (t) => t.template_name === "birthday_greet"
    );
    if (!birthdayTemplate) return;

    setActionNodes((prev) =>
      prev.map((node) => {
        // ← ADD "Send Birthday Message" to the check
        if (
          (node.type !== "Send Message Template" && node.type !== "Send Birthday Message") ||
          (node.config?.templateName && node.config.templateName !== "birthday_greet")
        ) {
          return node;
        }
        return {
          ...node,
          config: {
            ...node.config,
            templateId: birthdayTemplate.id,
            templateName: birthdayTemplate.template_name,
            template: birthdayTemplate.template_name,
            language: birthdayTemplate.language ?? "en_US",
            channel: node.config?.channel || birthdayTemplate.channel || "WhatsApp",
            waba_id: birthdayTemplate.waba_id ?? "",
            category: birthdayTemplate.category ?? "",
            wa_template_id: birthdayTemplate.wa_template_id ?? "",
          },
        };
      })
    );
  }, [templates]);

  // Replace the existing useEffect that handles the preset actions with this:
  // ── Generic Preset Actions Handler ─────────────────────────────────────
  useEffect(() => {
    const preset = location.state?.journeyPreset;
    if (!preset) return;

    // Skip special presets that already have dedicated makeXXXJourneyActions()
    const isLeadQualPreset = preset.title === "Lead Qualification" || preset.triggerType === "New Lead";
    const isBirthdayPreset = preset.title === "Birthday Campaign";
    const isAppointmentPreset = preset.title === "Appointment Reminder" || preset.triggerType === "On Event";
    const isFeedbackCollectionPreset = preset.title === "Feedback Collection";

    if (isLeadQualPreset || isBirthdayPreset || isAppointmentPreset || isFeedbackCollectionPreset) {
      setActiveTab("Actions");
      return;
    }

    // Set Trigger
    const triggerNode = makeTriggerNode(preset.triggerType || "On Message");
    if (triggerNode) {
      setTriggerNode(triggerNode);
    }

    // RCS "Prewired" preset — same example flow the old RCS Bot Studio's
    // starterFlow() shipped: template → wait for reply → default/chip replies.
    // "Blank" RCS just gets the trigger with no actions, like every other
    // blank preset.
    if (preset.triggerType === "RCS Trigger Node") {
      if (preset.presetActions?.length) {
        const templateNode = makeActionNode("Send RCS Template", { template_id: "", template_variables: {} }, 0);
        const defaultReply = makeActionNode("Send Text", { replyText: "Thanks for your message — we'll be in touch soon." }, 1);
        const chipReply = makeActionNode("Send Text", { replyText: "Great choice — someone from our team will follow up." }, 2);
        const waitNode = makeActionNode(
          "Wait for Reply",
          { true_next: chipReply.id, false_next: defaultReply.id },
          3,
        );
        setActionNodes([templateNode, waitNode, defaultReply, chipReply]);
      }
      setActiveTab("Actions");
      return;
    }

    // Build actions from presetActions array
    if (preset.presetActions && preset.presetActions.length > 0) {
      const defaultActions: ActionNode[] = [];

      preset.presetActions.forEach((actionType: string, index: number) => {
        const defaultConfig = getDefaultConfigForActionType(actionType);

        defaultActions.push(
          makeActionNode(actionType, defaultConfig, index)
        );
      });

      setActionNodes(defaultActions);
    }

    setActiveTab("Actions");
  }, [location.state]);
  // Also add this useEffect to ensure the action nodes are set when the component mounts with a preset
  useEffect(() => {
    const preset = location.state?.journeyPreset;

    // Skip for Lead Qualification + Birthday — handled by makeLeadQualificationJourneyActions /
    // makeBirthdayJourneyActions in the earlier useEffect with full 4-card wiring.
    const isLeadQualPreset =
      preset?.title === "Lead Qualification" ||
      preset?.triggerType === "New Lead";
    const isBirthdayPreset = preset?.title === "Birthday Campaign";
    if (isLeadQualPreset || isBirthdayPreset) return;

    // If we have a preset with actions and no existing action nodes, set them
    if (preset?.presetActions && preset.presetActions.length > 0 && actionNodes.length === 0) {
      const defaultActions: ActionNode[] = [];

      preset.presetActions.forEach((actionType: string, index: number) => {
        if (actionType === "Send Email") {
          defaultActions.push(
            makeActionNode("Send Email", {
              subject: "",
              body: "",
              to: ""
            }, index)
          );
        }
        else if (actionType === "Set Delay Time") {
          defaultActions.push(
            makeActionNode(
              "Set Delay Time",
              {
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
                delayValue: 0,
                delayUnit: "Days"
              },
              index
            )
          );
        }
        else if (actionType === "Condition") {
          defaultActions.push(
            makeActionNode(
              "Condition",
              {
                customerAttribute: "email_opened",
                condition: "equals",
                value: "true",
                true_next: "",
                false_next: ""
              },
              index
            )
          );
        }

        else if (actionType === "Send Message Template") {
          defaultActions.push(
            makeActionNode(
              "Send Message Template",
              {
                channel: "WhatsApp",
                templateId: "",
                templateName: "",
                language: "en_US",
                variables: {},
              },
              index
            )
          );
        }

        else if (actionType === "WhatsApp Message") {
          defaultActions.push(
            makeActionNode(
              "WhatsApp Message",
              {
                message: "",
                language: "",
                channel: "WhatsApp",
                waba_id: "",
                variables: "",
              },
              index
            )
          );
        }

      });

      if (defaultActions.length > 0) {
        setActionNodes(defaultActions);
      }
    }
  }, [location.state, actionNodes.length]);

  // Best-effort load of an RCS bot into the same canvas model — used when
  // /journeys/{id} 404s (this id belongs to the other backend instead).
  const loadRcsBotForEdit = async (botId: string) => {
    try {
      const [bot, status] = await getRcsBot(undefined, undefined, botId);
      if (status >= 400 || !bot?.id) {
        toast.error("Something went wrong while loading the bot.");
        return;
      }
      setBotName(bot.name || "Add a bot name");
      setRcsBotId(String(bot.id));
      const definition = bot.draft_definition || bot.published_definition;
      if (definition) {
        const { triggerNode: t, actionNodes: a } = rcsFlowToActionNodes(definition);
        setTriggerNode(t);
        setActionNodes(a);
      } else {
        setTriggerNode({ type: "RCS Trigger Node" });
      }
    } catch {
      toast.error("Something went wrong while loading the bot.");
    }
  };

  // ── Load existing journey (falls back to an RCS bot if this id isn't a
  // WhatsApp journey — the two backends share one edit URL, /journey/new/:id) ──
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [response, status] = await getJourneyById();
        const fetched: BotJourney = response?.data || response?.results || response;
        if (status >= 400 || !fetched?.id) {
          await loadRcsBotForEdit(id);
          return;
        }
        setBotName(fetched.name || "Add a bot name");
        setJourneyId(String(fetched.id));

        const topLevelSteps = (fetched as any).definition?.steps ?? [];
        const draftVersion = fetched.versions?.find((v: any) => v.status === "draft");
        const publishedVersion = fetched.versions?.find((v: any) => v.status === "published");
        const activeVersion = draftVersion || publishedVersion || fetched.versions?.[0];
        const fallbackSteps = activeVersion?.definition?.steps ?? [];
        const steps = topLevelSteps.length ? topLevelSteps : fallbackSteps;
        const activeVersionNumber =
          (fetched as any).current_draft_version ??
          (fetched as any).version ??
          activeVersion?.version ??
          null;

        if (activeVersionNumber) {
          setVersionId(activeVersionNumber);
        }

        if (steps.length > 0) {
          setStepId(steps[0].id);
        }

        const { triggerNode: t, actionNodes: a } = hydrateJourneyToCanvas(fetched);
        setTriggerNode(t);
        setActionNodes(a);
      } catch {
        toast.error("Something went wrong while loading the journey.");
      }
    })();
  }, [id]);

  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  const handleDropTrigger = (type: string) => {
    const nextTrigger = makeTriggerNode(type);
    if (!nextTrigger) {
      toast.error(`Trigger "${type}" is not supported yet.`);
      return;
    }

    setTriggerNode(nextTrigger);
    setActiveTab("Actions");
  };

  const handleDropAction = (type: string) => {
    const presets: Record<string, ActionNode["config"]> = {
      "Assign Agent": { agentId: "" },
      "Add or Remove Tags": { action: "Add Tags", tags: [] },
      "Send Message": { message: "", media_url: null, media_type: null },
      "Add to Broadcast": { template: "hello_world", language: "en_US", components: [] },
      "Fetch Current Date": { dateSource: "current_date", dateFormat: "dd-mm-yyyy" },
      "Fetch Audience": {
        dataSource: "csv",
        dateColumn: "Birthday_Date",
        dateFormat: "dd-mm-yyyy",
        templateName: "birthday_greet",
        language: "en_US",
      },
      "Call API": { url: "", method: "POST" },
      "Close Conversation": {},
      "Run at Specific Time": makeScheduledConfig(),
      "Wait Until": {
        waitMode: "date",
        dateAttribute: "Appointment_Date",
        offsetValue: 1,
        offsetUnit: "Days",
        offsetDirection: "Before",
      },
      "Mark as Sent": { dataSource: "csv", updateColumn: "Last_wished_on", updateValue: "Today" },
      "Set Customer Attribute": { attribute: "User Response", value: "" },
      "Condition": {
        customerAttribute: "user_response",
        condition: "equals",
        value: "",
        true_next: "",
        false_next: ""
      },
      "Send Email": {
        subject: "",
        body: "",
        to: ""
      },
      "WhatsApp Message": {
        channel: "WhatsApp",
        templateId: "",
        templateName: "",
        language: "en_US",
        variables: {},
      },
      "Set Delay Time": {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      "Send Birthday Message": {
        channel: "",        // user picks channel first
        templateId: "",
        templateName: "",
        language: "en_US",
        variables: {},
      },
      "Send RCS Template": { template_id: "", template_variables: {} },
      "Send Menu": { replyText: "", chips: [] },
      "Send Carousel": { replyText: "", cards: [] },
      "Send Text": { replyText: "" },
      "Wait for Reply": { true_next: "", false_next: "" },
      "Set Variable": { variable: "", value: "" },
      "Escalate to Human": { transfer_reason: "" },
      "Confirm Order": { replyText: "" },
      "Branch": { variable: "", equals: "", true_next: "", false_next: "" },
    };

    const newNode: ActionNode = {
      id: Math.random().toString(36).slice(2),
      type: type as ActionNode["type"],
      config: presets[type] || {},
      x: 200,
      y: 200,
    };

    setActionNodes((prev) => {
      // If there's a recent Condition (or RCS "Wait for Reply"/"Branch", which
      // fork the same way) with an unwired true_next or false_next, auto-wire
      // this new node into the open branch. Otherwise append linearly.
      const lastCondition = [...prev].reverse().find(
        (n) => ["Condition", "Wait for Reply", "Branch"].includes(n.type) &&
          (!n.config?.true_next || !n.config?.false_next)
      );

      if (lastCondition) {
        const hasTrueNext = Boolean(lastCondition.config?.true_next);
        const hasFalseNext = Boolean(lastCondition.config?.false_next);
        const branchKey = !hasTrueNext ? "true_next" : !hasFalseNext ? "false_next" : null;

        if (branchKey) {
          return [
            ...prev.map((n) =>
              n.id === lastCondition.id
                ? { ...n, config: { ...n.config, [branchKey]: newNode.id } }
                : n
            ),
            newNode,
          ];
        }
      }

      return [...prev, newNode];
    });
  };


  const handleRemoveTrigger = async () => {
    const [response] = await deleteTrigger();

    if (response?.status) {
      toast.success(response.message || "Trigger deleted!");
      setTriggerNode(null);
      setActionNodes([]);
    } else {
      toast.error(response?.detail || "Failed to delete trigger");
    }
  };

  // ── Remove / update action nodes ───────────────────────────────────────────
  const handleRemoveAction = async (nodeId: string) => {
    // Immediate local removal (works even before saving)
    setActionNodes((prev) => prev.filter((node) => node.id !== nodeId));

    // Optional: Only call API if the journey is already created and saved
    if (journeyId && versionId) {
      setStepId(nodeId); // for API deletion
      const [response] = await deleteAction(null);
      if (response?.status) {
        toast.success(response.message || "Action deleted successfully!");
      } else {
        toast.error(response?.detail || "Failed to delete action from server");
      }
    }
  };

  const handleUpdateAction = (nodeId: string, config: Record<string, any>) =>
    setActionNodes((prev) => prev.map((n) => (n.id === nodeId ? { ...n, config } : n)));

  // ── Autosave ───────────────────────────────────────────────────────────────
  // Persist the canvas shortly after the operator stops changing it, so a
  // template or channel pick survives a refresh without them remembering to
  // press Save draft.
  //
  // Debounced rather than fired per keystroke: a text field would otherwise
  // issue a request per character. 1.2s is long enough to coalesce a burst
  // of edits and short enough that a pick feels saved before you look away.
  //
  // Skipped for RCS (separate backend, own draft endpoint) and until the
  // journey exists — there is nothing to PUT a draft into before creation.
  useEffect(() => {
    if (!journeyId || isRcsTrigger) return;
    if (!triggerNode && actionNodes.length === 0) return;
    const t = setTimeout(async () => {
      try {
        const payload: any = buildJourneyPayload(
          botName, triggerNode, actionNodes, templates,
          { channel: triggerNode?.channel as string | undefined }
        );
        const definition = payload?.definition ?? payload;
        if (!definition) return;
        const [res] = await autosaveDraft({ definition });
        // Silent on failure: autosave is a convenience, and a toast on every
        // transient blip would train the operator to ignore real errors.
        if (res && !res.detail) {
          setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
        }
      } catch { /* keep editing */ }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, isRcsTrigger, botName, triggerNode, actionNodes, templates]);

  // ── ConvoAI ────────────────────────────────────────────────────────────────
  const handleConvoAISend = async (_msg: string, _hist: ConvoAIMessage[]): Promise<string> =>
    "I'll help you build your bot! What would you like to configure?";

  const uploadBirthdayAudienceFiles = async (targetJourneyId: string) => {
    const audienceNodes = actionNodes.filter(
      (node) => node.type === "Fetch Audience" && node.config?.file instanceof File
    );

    for (const node of audienceNodes) {
      const formData = new FormData();
      formData.append("file", node.config.file);
      formData.append("journey_id", String(targetJourneyId));
      formData.append("template_name", node.config?.templateName ?? "birthday_greet");
      formData.append("language", node.config?.language ?? "en_US");

      const [response, status = 500] = await broadcastExcelUploadRequest.makeRequest(formData);
      if (status >= 400 || response?.status === "error") {
        toast.error(response?.message || response?.detail || "Birthday audience upload failed.");
        continue;
      }

      setActionNodes((prev) =>
        prev.map((item) =>
          item.id === node.id
            ? {
              ...item,
              config: {
                ...item.config,
                file: undefined,
                uploadStatus: "submitted",
                uploadResponse: response,
                totalContacts: response?.total_contacts,
                submitted: response?.submitted,
                skipped: response?.skipped,
              },
            }
            : item
        )
      );
      toast.success(`Birthday audience submitted${response?.submitted ? `: ${response.submitted}` : ""}.`);
    }
  };

  // ── RCS save draft / publish — separate backend, separate payload shape.
  // Kept fully independent of the WhatsApp handlers below rather than
  // threading conditionals through their validation/dedupe/test-event logic.
  const handleRcsSaveDraft = async () => {
    if (!botName.trim() || botName === "Add a bot name") {
      toast.error("Please give your bot a name before saving.");
      return;
    }
    setRcsSaving(true);
    try {
      const flow = actionNodesToRcsFlow(botName, triggerNode, actionNodes);
      if (rcsBotId) {
        const [json, status] = await updateRcsDraft({ definition: flow }, undefined, `${rcsBotId}/draft`);
        if (status >= 400) {
          toast.error(json?.detail || "Failed to save draft.");
          return;
        }
      } else {
        const [created, status] = await createRcsBot({
          tenant_id: rcsTenantId,
          name: botName.trim(),
          definition: flow,
        });
        if (status >= 400) {
          toast.error(created?.detail || "Failed to save draft.");
          return;
        }
        setRcsBotId(String(created.id));
        navigate(`/journey/new/${created.id}`, { replace: true });
      }
      toast.success("Draft saved!");
    } finally {
      setRcsSaving(false);
    }
  };

  const handleRcsPublish = async () => {
    if (!rcsTenantId) {
      toast.error("Still resolving your workspace. Please retry in a moment.");
      return;
    }
    if (actionNodes.length === 0) {
      toast.error("Please add at least one action.");
      return;
    }
    setRcsPublishing(true);
    try {
      const flow = actionNodesToRcsFlow(botName, triggerNode, actionNodes);
      let targetId = rcsBotId;
      if (!targetId) {
        const [created, status] = await createRcsBot({
          tenant_id: rcsTenantId,
          name: botName.trim() || "New RCS Bot",
          definition: flow,
        });
        if (status >= 400) {
          toast.error(created?.detail || "Failed to publish.");
          return;
        }
        targetId = String(created.id);
        setRcsBotId(targetId);
        navigate(`/journey/new/${targetId}`, { replace: true });
      } else {
        const [json, status] = await updateRcsDraft({ definition: flow }, undefined, `${targetId}/draft`);
        if (status === 404) {
          // The bot was deleted between load and publish (or the id
          // never existed on this backend). Recover by creating a fresh
          // bot with the current canvas — user's work is preserved.
          const [created, createStatus] = await createRcsBot({
            tenant_id: rcsTenantId,
            name: botName.trim() || "New RCS Bot",
            definition: flow,
          });
          if (createStatus >= 400) {
            toast.error(created?.detail || "This bot no longer exists and could not be recreated.");
            return;
          }
          targetId = String(created.id);
          setRcsBotId(targetId);
          navigate(`/journey/new/${targetId}`, { replace: true });
        } else if (status >= 400) {
          toast.error(json?.detail || "Failed to publish.");
          return;
        }
      }
      const [res, publishStatus] = await publishRcsBot(undefined, undefined, `${targetId}/publish`);
      if (publishStatus === 404) {
        toast.error("This bot no longer exists. Save a draft first, then publish.");
        setRcsBotId(null);
        return;
      }
      if (publishStatus >= 400) {
        toast.error(res?.detail || "Failed to publish.");
        return;
      }
      toast.success(res?.published_version ? `Published v${res.published_version}` : "Bot published!");
      navigate("/journey");
    } finally {
      setRcsPublishing(false);
    }
  };

  const handleRcsDelete = async () => {
    if (!rcsBotId) return;
    // Confirm before a destructive backend op. Native window.confirm works
    // in every browser Twilio/support customers use (Chrome / Safari /
    // Firefox / Edge / mobile), no toast library dependency.
    if (!window.confirm(
      "Delete this bot permanently? This removes the bot, all versions, and all sessions. This cannot be undone.",
    )) return;
    setRcsDeleting(true);
    try {
      const [res, status] = await deleteRcsBot(undefined, undefined, rcsBotId);
      if (status === 404) {
        // Already gone. Treat as success so the operator can move on.
        toast.success("Bot already removed.");
        navigate("/journey");
        return;
      }
      if (status >= 400) {
        toast.error(res?.detail || "Failed to delete.");
        return;
      }
      toast.success("Bot deleted.");
      navigate("/journey");
    } finally {
      setRcsDeleting(false);
    }
  };

  // ── Save draft ─────────────────────────────────────────────────────────────
  // For an EXISTING journey we do two things:
  //   1. POST a fresh draft version at /journeys/{id}/versions with the
  //      current canvas definition — this is the ONLY path that persists
  //      actionNodes changes (Condition channel switch, whatsapp_actions
  //      rows, template picks). Without it, saveDraft only PATCHes trigger
  //      config and every publish keeps promoting the same stale definition.
  //   2. PATCH /journeys/{id}/trigger with the trigger flags (preserves
  //      the prior behavior for existing consumers).
  // For a NEW journey the PATCH becomes a POST that creates it (existing
  // behavior — the `saveDraftJourney` hook flips URL/method on journeyId).
  const handleSaveDraft = handleSubmit(async () => {
    if (!botName.trim() || botName === "Add a bot name") {
      toast.error("Please give your bot a name before saving.");
      return;
    }

    // 1. Persist the definition itself when we already have a journey id.
    if (journeyId) {
      const built = buildJourneyPayload(botName, triggerNode, actionNodes, templates);
      const [versionResp, versionStatus] = await createVersion({ definition: built.definition });
      if (versionStatus && versionStatus >= 400) {
        toast.error(versionResp?.detail ?? versionResp?.message ?? "Failed to save definition.");
        return;
      }
      const newVersion = versionResp?.version ?? versionResp?.data?.version;
      if (newVersion) {
        setVersionId(newVersion);
        try {
          const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
          stored.current_draft_version = newVersion;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        } catch { /* localStorage best-effort */ }
      }
    }

    // 2. Trigger config PATCH (or initial-create POST when journeyId absent).
    const payload = {
      "restart_journey": true,
      "match_similar_all": true
    }
    const [response] = await saveDraftJourney(payload);
    const newId = response?.id ?? response?._id ?? response?.journeyId;
    if (newId) {
      setJourneyId(String(newId));
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: newId, ...response }));
    }
    toast.success("Draft saved!");
  });

  // Add these validation helper functions after the imports and before the BotBuilderPage component:

  // ─── Validation Helpers ──────────────────────────────────────────────────────

  const validateActionNodes = (actionNodes: ActionNode[], templates: TemplateModule[]): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    for (const node of actionNodes) {
      // Check for actions that require template selection
      if (node.type === "Send Message Template" || node.type === "Send Birthday Message" || node.type === "WhatsApp Message") {
        const hasTemplateId = node.config?.templateId && node.config.templateId.trim() !== "";
        const hasTemplateName = node.config?.templateName && node.config.templateName.trim() !== "";

        if (!hasTemplateId && !hasTemplateName) {
          errors.push(`Please select a template for "${node.type}" action`);
        }
      }

      // Check for Send Email template — WhatsApp channel picks its
      // templates on the downstream send nodes (Condition's whatsapp_actions
      // rows), so this upstream card doesn't need a template selection.
      if (node.type === "Send Email" && node.config?.channel !== "WhatsApp") {
        const hasTemplateName = node.config?.templateName && node.config.templateName.trim() !== "";
        if (!hasTemplateName) {
          errors.push(`Please select a template for "${node.type}" action`);
        }
      }

      // Check for Condition action - all conditions must be properly configured
      if (node.type === "Condition") {
        // Check if there are conditions configured
        const conditions = node.config?.conditions;
        const customerAttribute = node.config?.customerAttribute;
        const customerAttributes = node.config?.customerAttributes; // multi-attr
        const condition = node.config?.condition;
        const value = node.config?.value;
        const isWhatsApp = node.config?.channel === "WhatsApp";
        const whatsappActions = node.config?.whatsapp_actions;

        // For birthday variant
        if (node.config?.variant === "birthday") {
          if (!conditions || conditions.length === 0) {
            errors.push(`Please configure all conditions for "${node.type}" action`);
          }
        }
        // WhatsApp path — validated against whatsapp_actions rows, not
        // operator/value. Each row must carry an attribute + template
        // (per-row wait is optional; 0 = immediate).
        else if (isWhatsApp) {
          const attrs: string[] = Array.isArray(customerAttributes) && customerAttributes.length > 0
            ? customerAttributes
            : (customerAttribute ? [customerAttribute] : []);
          if (attrs.length === 0) {
            errors.push(`Please pick at least one attribute for "${node.type}" action`);
          }
          const rows: any[] = Array.isArray(whatsappActions) ? whatsappActions : [];
          if (rows.length === 0 || rows.some((r) => !r?.template || !String(r.template).trim())) {
            errors.push(`Please pick a WhatsApp template for each attribute on "${node.type}" action`);
          }
        }
        // Email / default regular condition
        else {
          if (!customerAttribute || customerAttribute.trim() === "") {
            errors.push(`Please select a customer attribute for "${node.type}" action`);
          }
          if (!condition || condition.trim() === "") {
            errors.push(`Please select a condition operator for "${node.type}" action`);
          }
          // If condition is not "exists", value is required
          if (condition !== "exists" && condition !== "not_exists" && (value === undefined || value === null || String(value).trim() === "")) {
            errors.push(`Please enter a value for "${node.type}" action`);
          }
        }
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  const validatePublishGate = (nodes: ActionNode[]): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    nodes.forEach((node, index) => {
      const label = `${node.type} #${index + 1}`;

      if (node.type === "Send Message Template" || node.type === "Send Birthday Message" || node.type === "WhatsApp Message") {
        const templateName = node.config?.templateName ?? node.config?.template ?? "";
        if (!String(templateName).trim()) {
          errors.push(`${label} needs a template before publishing.`);
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  const validateTriggerNode = (triggerNode: TriggerNode | null): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (!triggerNode) {
      errors.push("Please add a trigger before creating the journey");
      return { isValid: false, errors };
    }

    if (triggerNode.type === "On Message") {
      const hasKeyword = (triggerNode.keywords ?? []).some((k) => k.text.trim() !== "");
      if (!hasKeyword) {
        errors.push("Please enter at least one keyword for the trigger");
      }
    }

    if (triggerNode.type === "Run at Specific Time") {
      if (!triggerNode.fixedTime) {
        errors.push("Please choose a date and time for the scheduled trigger");
      }
    }

    if (triggerNode.type === "On Event") {
      if (!String(triggerNode.eventType ?? "").trim()) {
        errors.push("Please select an event type for the trigger");
      }
    }

    return { isValid: errors.length === 0, errors };
  };

  // Then update the handleCreateJourney function:

  const handleCreateJourney = handleSubmit(async () => {
    // Validate Trigger
    const triggerValidation = validateTriggerNode(triggerNode);
    if (!triggerValidation.isValid) {
      triggerValidation.errors.forEach(error => toast.error(error));
      return;
    }

    // Validate Actions
    const actionValidation = validateActionNodes(actionNodes, templates);
    if (!actionValidation.isValid) {
      actionValidation.errors.forEach(error => toast.error(error));
      return;
    }

    if (actionNodes.length === 0) {
      toast.error("Please add at least one action.");
      return;
    }

    if (!botName.trim() || botName === "Add a bot name") {
      toast.error("Please give your bot a name.");
      return;
    }

    // Pass templates to buildJourneyPayload
    const payload = buildJourneyPayload(botName, triggerNode, actionNodes, templates);
    const [response] = await createJourney(payload);

    if (response?.status) {
      const newId =
        response?.id ??
        response?._id ??
        response?.journeyId;

      const version =
        response?.current_draft_version ??
        response?.version ??
        response?.versions?.[0]?.version;

      const steps =
        response?.definition?.steps ??
        response?.versions?.[0]?.definition?.steps ??
        [];

      setJourneyId(String(newId));

      if (version) {
        setVersionId(version);
      }

      if (steps.length) {
        setStepId(steps[0].id);
      }

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          id: newId,
          versionId: version,
          steps,
          ...response,
        })
      );

      toast.success("Journey created successfully!");

      await uploadBirthdayAudienceFiles(String(newId));
    } else {
      toast.error(response?.message ?? response?.detail ?? "Failed to create journey.");
    }
  });


  // ── Publish ────────────────────────────────────────────────────────────────
  // Two steps:
  //   1. Mark the version published
  //   2. Archive any OTHER published journey for this tenant that would
  //      fan out on the same trigger event (same name OR shared
  //      event_type). Without step 2, a single external event spawns N
  //      workflows → N duplicate WhatsApp sends (Joseph 06-30 pain).
  // Event firing lives elsewhere: the trigger card's "Send test event"
  // button uses useJouneyReq for a decoupled, single-purpose action.
  const handlePublish = handleSubmit(async () => {
    if (!journeyId) { toast.error("No journey found to publish."); return; }
    const publishValidation = validatePublishGate(actionNodes);
    if (!publishValidation.isValid) {
      publishValidation.errors.forEach((error) => toast.error(error));
      return;
    }

    // Push the CURRENT canvas definition as a fresh draft version FIRST.
    // Otherwise publish promotes whatever the last saved version is —
    // which for edits is the initial-create definition, so any changes
    // in the Condition side-panel (channel, whatsapp_actions, templates,
    // waits) never make it into the published definition. Joseph
    // 2026-08-04: "still not saving the what conditions i published".
    let versionToPublish: number | null | undefined = currentDraftVersion || versionId;
    const built = buildJourneyPayload(botName, triggerNode, actionNodes, templates);
    const [versionResp, versionStatus] = await createVersion({ definition: built.definition });
    if (versionStatus && versionStatus >= 400) {
      toast.error(versionResp?.detail ?? versionResp?.message ?? "Failed to save definition before publish.");
      return;
    }
    const newVersion = versionResp?.version ?? versionResp?.data?.version;
    if (newVersion) {
      versionToPublish = newVersion;
      setVersionId(newVersion);
      try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        stored.current_draft_version = newVersion;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch { /* localStorage best-effort */ }
    }

    const payload = {
      keywords: 'hello',
      version: versionToPublish,
    }
    const [response] = await publishJourney(payload);
    if (response?.status) {
      toast.success(response.message || "Bot published successfully!");
      // Dedupe prior published bots + fire test events. Both use
      // useJouneyReq under the hood, which is proven-working. Backend
      // is now safe (tz + event-attrs + safety-net fixes shipped),
      // so this can't cause the earlier duplicate/immediate-fire
      // problems.
      await archivePriorPublishedDuplicates(journeyId);
      await fireTestLeadsAsEvents();
      navigate("/journey");
      localStorage.removeItem(STORAGE_KEY);
    } else {
      toast.error(response?.message ?? response?.detail ?? "Failed to publish journey.");
    }
  });

  // Fires one /v1/events/external POST per saved lead on the trigger
  // card. Silent no-op when trigger is not an On Event with a
  // non-empty testLeads array. Used by handlePublish so publishing
  // an appointment/feedback bot with test leads immediately sends
  // the bot to those phones for verification.
  async function fireTestLeadsAsEvents() {
    if (!triggerNode || triggerNode.type !== "On Event") return;
    const leads: any[] = Array.isArray((triggerNode as any).testLeads)
      ? (triggerNode as any).testLeads
      : [];
    const filled = leads.filter((l) => l?.phone && l?.first_name);
    if (filled.length === 0) return;

    // Resolve tenant_id (JWT → cache → auth service)
    let tenantId = "";
    try {
      const { jwtDecode } = await import("jwt-decode");
      const decoded: any = jwtDecode(token || "");
      tenantId = decoded?.tenant_id ?? decoded?.tenantId ?? decoded?.tenant ?? decoded?.org_id ?? "";
    } catch { /* ignore */ }
    if (!tenantId) tenantId = localStorage.getItem("linkedin_tenant_id") ?? "";
    if (!tenantId) {
      try {
        const authUrl = process.env.REACT_APP_AUTH_URL || "https://api.connect.dalmlabs.com";
        const r = await fetch(`${authUrl}/auth/get_userdata`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          tenantId = d?.payload?.org_id ?? d?.payload?.tenant_id ?? d?.org_id ?? d?.tenant_id ?? "";
          if (tenantId) localStorage.setItem("linkedin_tenant_id", tenantId);
        }
      } catch { /* ignore */ }
    }
    if (!tenantId) { toast.error("Published but tenant_id lookup failed — bot not fired"); return; }

    // Pick the primary event type: appointment-style if present, else
    // whatever's first.
    const APPT = new Set(["Appointment_Scheduled", "Appointment_Reminder", "Appointment_Missed"]);
    const evList: string[] = Array.isArray((triggerNode as any).eventTypes)
      ? (triggerNode as any).eventTypes
      : [(triggerNode as any).eventType].filter(Boolean);
    const eventType = evList.find((e) => APPT.has(e)) || evList[0] || "Appointment_Scheduled";
    const now = new Date().toISOString();

    let ok = 0, fail = 0;
    for (const l of filled) {
      // Convert lead's local date/time to UTC ISO (browser TZ if no tz picked)
      let apptISO: string | undefined = undefined;
      let readable: string | undefined = undefined;
      const dateStr = String(l.appointment_date ?? "").trim();
      const timeStr = String(l.appointment_time ?? "").trim();
      const tz: string = l.timezone || "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr) && /^\d{2}:\d{2}$/.test(timeStr)) {
        try {
          if (!tz) {
            apptISO = new Date(`${dateStr}T${timeStr}:00`).toISOString();
          } else {
            const [y, m, day] = dateStr.split("-").map(Number);
            const [h, min]    = timeStr.split(":").map(Number);
            const utcGuess = Date.UTC(y, m - 1, day, h, min);
            const fmt = new Intl.DateTimeFormat("en-US", {
              timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
              hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
            });
            const parts = Object.fromEntries(
              fmt.formatToParts(new Date(utcGuess)).filter((p) => p.type !== "literal")
                .map((p) => [p.type, p.value])
            ) as Record<string, string>;
            const tzUTC = Date.UTC(
              Number(parts.year), Number(parts.month) - 1, Number(parts.day),
              Number(parts.hour) % 24, Number(parts.minute), Number(parts.second ?? "0"),
            );
            apptISO = new Date(utcGuess - (tzUTC - utcGuess)).toISOString();
          }
          if (apptISO) {
            readable = new Date(apptISO).toLocaleString(undefined, {
              timeZone: tz || undefined,
              day: "2-digit", month: "short", year: "numeric",
              hour: "numeric", minute: "2-digit",
            });
          }
        } catch { /* ignore, apptISO stays undefined */ }
      }

      const body = {
        tenant_id: tenantId,
        event_type: eventType,
        external_id: `publish_${String(l.first_name).toLowerCase()}_${Date.now()}_${ok + fail}`,
        occurred_at: now,
        contact: {
          phone: String(l.phone).trim(),
          first_name: String(l.first_name).trim(),
          last_name: l.last_name ? String(l.last_name).trim() : undefined,
        },
        attributes: {
          appointment_date: apptISO,
          scheduled_date_and_time: readable,
          timezone: l.timezone || undefined,
        },
      };
      const [resp, statusCode] = await postExternalEvent(body);
      if (statusCode >= 200 && statusCode < 300) ok++;
      else {
        fail++;
        // eslint-disable-next-line no-console
        console.error(`[Publish] event POST HTTP ${statusCode}`, resp, "body=", body);
      }
    }
    if (ok > 0 && fail === 0) toast.success(`Bot sent to ${ok} phone${ok === 1 ? "" : "s"}`);
    else if (ok > 0) toast.warning(`Bot sent to ${ok} phone${ok === 1 ? "" : "s"}, ${fail} failed`);
    else toast.error(`Bot published but ${fail} test send${fail === 1 ? "" : "s"} failed`);
  }

  // Fetch all published bots for this tenant. Archive any whose name
  // matches (case-insensitive) OR whose trigger event_type overlaps
  // with the one we just published. Uses raw fetch with the same
  // header pattern useJouneyReq uses (Content-Type, tenant, Authorization).
  async function archivePriorPublishedDuplicates(currentId: string) {
    const backend = process.env.REACT_APP_BACKEND_URL || "https://journeybuilder.connect.dalmlabs.com";
    const authHeader = { "Content-Type": "application/json", tenant: "default", Authorization: `Bearer ${token}` };
    const myEventTypes = new Set<string>(
      (Array.isArray((triggerNode as any)?.eventTypes) ? (triggerNode as any).eventTypes : [])
        .concat([(triggerNode as any)?.eventType])
        .filter((v: any) => typeof v === "string" && v.trim())
        .map((v: string) => v.trim().toLowerCase())
    );
    const myName = (botName || "").trim().toLowerCase();

    try {
      const res = await fetch(`${backend}/journeys?per_page=500`, { headers: authHeader });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn(`[Publish] /journeys list HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      const list: any[] = Array.isArray(data)
        ? data
        : (data?.items ?? data?.journeys ?? data?.data ?? []);
      const toArchive = list.filter((j) => {
        if (String(j?.id) === String(currentId)) return false;
        if (j?.status !== "published") return false;
        // Same bot name
        if (myName && (j?.name ?? "").trim().toLowerCase() === myName) return true;
        // Same trigger event_type
        const cfg = j?.trigger_config ?? j?.triggerConfig ?? {};
        const theirEventTypes: string[] = [
          ...(Array.isArray(cfg.event_types) ? cfg.event_types : []),
          cfg.event_type,
          j?.event_type,
          j?.trigger?.event_type,
        ].filter((v: any) => typeof v === "string" && v.trim());
        return theirEventTypes.some((t) => myEventTypes.has(t.toLowerCase()));
      });
      // eslint-disable-next-line no-console
      console.log(`[Publish] Archiving ${toArchive.length} duplicate(s):`, toArchive.map(j => ({id: j.id, name: j.name})));
      for (const j of toArchive) {
        try {
          const patchRes = await fetch(`${backend}/journeys/${j.id}`, {
            method: "PATCH",
            headers: authHeader,
            body: JSON.stringify({ status: "archived" }),
          });
          if (!patchRes.ok) {
            // eslint-disable-next-line no-console
            console.warn(`[Publish] PATCH archive j${j.id} HTTP ${patchRes.status}`);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`[Publish] PATCH archive j${j.id} threw:`, e);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Publish] archivePriorPublishedDuplicates threw:", e);
    }
  }


  // ── Reset ──────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setBotName("Add a bot name");
    setTriggerNode(null);
    setActionNodes([]);
    setJourneyId(null);
    setSelectedActionId(null);
    setActiveTab("Triggers");
    localStorage.removeItem(STORAGE_KEY);
  };

  const isBusy = !!loading || !!publishLoader || !!draftLoader;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <button onClick={() => navigate("/journey")} className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <span className="hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer" onClick={() => navigate("/journey")}>
            All Bots
          </span>
          <span>/</span>
          <span className="font-semibold text-gray-800 dark:text-gray-200">New Bot</span>
        </div>
      </div>

      {/* Sub-header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
        <div>
          {editingName ? (
            <input
              autoFocus
              className="text-lg font-semibold text-gray-800 dark:text-gray-200 bg-transparent border-b border-indigo-400 outline-none"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
            />
          ) : (
            <button
              className="flex items-center gap-1.5 text-lg font-semibold text-gray-800 dark:text-gray-200"
              onClick={() => setEditingName(true)}
            >
              {botName === "Add a bot name"
                ? <span className="text-[#7B7B7B] font-semibold dark:text-gray-400">{botName}</span>
                : botName}
              <PencilIcon size={16} />
            </button>
          )}
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-400 mt-0.5">
            Set triggers and actions for your bot
            {savedAt && (
              // Autosave is silent by design; without this the operator has
              // no way to tell whether their pick was persisted.
              <span className="ml-2 text-green-600 dark:text-green-400">· Saved {savedAt}</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {!isRcsTrigger && (
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Reset Changes
            </button>
          )}
          {isRcsTrigger && rcsBotId && (
            <button
              onClick={handleRcsDelete}
              disabled={rcsDeleting || rcsSaving || rcsPublishing}
              className="px-4 py-2 text-sm font-medium text-rose-700 dark:text-rose-400 bg-white dark:bg-transparent border border-rose-300 dark:border-rose-500/40 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-60 rounded-lg transition-colors"
              title="Permanently remove the bot and all its versions and sessions"
            >
              {rcsDeleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <button
            onClick={isRcsTrigger ? handleRcsSaveDraft : handleSaveDraft}
            disabled={isRcsTrigger ? rcsSaving || rcsPublishing || rcsDeleting : isBusy}
            className="px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-white dark:bg-transparent border border-indigo-400 dark:border-indigo-500/40 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 disabled:opacity-60 rounded-lg transition-colors"
          >
            {isRcsTrigger ? (rcsSaving ? "Saving…" : "Save Draft") : draftLoader ? "Saving…" : "Save draft"}
          </button>
          {/* Dry-run the current canvas. Deliberately enabled on an unsaved
              draft — previewing before you commit is the point. Offered for
              every bot type: RCS flows are normalised server-side, so the
              same panel walks them. */}
          {(
            <button
              onClick={() => setPreviewOpen(true)}
              disabled={!triggerNode}
              title={triggerNode ? "Dry-run this bot without sending anything"
                                 : "Add a trigger first"}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg transition-colors"
            >
              Preview Bot <Play size={14} />
            </button>
          )}
          <button
            onClick={isRcsTrigger ? handleRcsPublish : isCreated ? handlePublish : handleCreateJourney}
            disabled={isRcsTrigger ? rcsSaving || rcsPublishing || rcsDeleting : isBusy}
            className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg font-medium transition-colors"
          >
            {isRcsTrigger
              ? rcsPublishing ? "Publishing…" : "Publish"
              : loading || publishLoader ? "Saving…" : isCreated ? "Publish Bot" : "Create Journey"}
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        <SidebarPanel activeTab={activeTab} setActiveTab={setActiveTab} triggerType={triggerNode?.type} />
        <Canvas
          triggerNode={triggerNode}
          actionNodes={actionNodes}
          templates={templates}
          onDropTrigger={handleDropTrigger}
          onDropAction={handleDropAction}
          onUpdateTrigger={setTriggerNode}
          onDeleteTrigger={handleRemoveTrigger}
          onResetTrigger={handleReset}
          onRemoveAction={handleRemoveAction}
          onUpdateAction={handleUpdateAction}
          mediaUpload={mediaUploadRequest.makeRequest}
          broadcastExcelUpload={broadcastExcelUploadRequest.makeRequest}
          journeyId={journeyId}
          selectedActionId={selectedActionId}
          onSelectAction={setSelectedActionId}
        />
        {selectedActionId && (
          <RcsConfigPanel
            node={actionNodes.find((n) => n.id === selectedActionId) ?? null}
            onUpdate={(config) => handleUpdateAction(selectedActionId, config)}
            onDelete={() => {
              handleRemoveAction(selectedActionId);
              setSelectedActionId(null);
            }}
            onClose={() => setSelectedActionId(null)}
            templates={templates}
            mediaUpload={mediaUploadRequest.makeRequest}
            broadcastExcelUpload={broadcastExcelUploadRequest.makeRequest}
            journeyId={journeyId}
            triggerEventType={(triggerNode?.eventType as string) ?? ""}
          />
        )}
        <ConvoAIPanel
          isOpen={showConvoAI}
          onClose={() => setShowConvoAI(false)}
          onSend={handleConvoAISend}
          greeting="Hi! I'm ConvoAI. I can help you build your bot. What would you like to configure?"
          quickActions={["Add a trigger", "Add a condition", "Add an action", "Configure tags", "Help me build a welcome bot", "Show me examples"]}
        />
      </div>

      {!showConvoAI && (
        <button
          onClick={() => setShowConvoAI(true)}
          className="fixed bottom-6 right-6 z-50 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 group"
          aria-label="Open ConvoAI"
        >
          <svg width="56" height="48" viewBox="0 0 56 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_1880_2477)">
              <rect x="4" y="4" width="48" height="40" rx="12" fill="#86EFAC" shape-rendering="crispEdges" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M28 14C28.901 14 29.774 14.12 30.605 14.344C30.0859 14.6166 29.658 15.0354 29.3743 15.5486C29.0906 16.0617 28.9635 16.6468 29.0087 17.2314C29.0539 17.816 29.2695 18.3746 29.6287 18.838C29.9879 19.3015 30.4751 19.6495 31.03 19.839L31.408 19.968C31.5528 20.0173 31.6844 20.0993 31.7926 20.2074C31.9007 20.3156 31.9827 20.4472 32.032 20.592L32.162 20.97C32.3519 21.5243 32.6999 22.0109 33.1631 22.3697C33.6264 22.7285 34.1845 22.9438 34.7687 22.989C35.3529 23.0342 35.9376 22.9073 36.4505 22.6241C36.9634 22.3408 37.3821 21.9135 37.655 21.395C37.8841 22.2444 38.0001 23.1203 38 24C38 29.523 33.523 34 28 34C26.11 34 24.34 33.475 22.832 32.563L19.8 33.454C19.6259 33.5052 19.4413 33.5086 19.2654 33.4638C19.0896 33.4189 18.9291 33.3276 18.8008 33.1992C18.6725 33.0709 18.5811 32.9104 18.5363 32.7346C18.4914 32.5587 18.4948 32.3741 18.546 32.2L19.438 29.168C18.4951 27.6093 17.9977 25.8218 18 24C18 18.477 22.477 14 28 14ZM24.5 22.5C24.1022 22.5 23.7207 22.658 23.4393 22.9393C23.158 23.2206 23 23.6022 23 24C23 24.3978 23.158 24.7794 23.4393 25.0607C23.7207 25.342 24.1022 25.5 24.5 25.5C24.8978 25.5 25.2794 25.342 25.5607 25.0607C25.842 24.7794 26 24.3978 26 24C26 23.6022 25.842 23.2206 25.5607 22.9393C25.2794 22.658 24.8978 22.5 24.5 22.5ZM31.5 22.5C31.1022 22.5 30.7207 22.658 30.4393 22.9393C30.158 23.2206 30 23.6022 30 24C30 24.3978 30.158 24.7794 30.4393 25.0607C30.7207 25.342 31.1022 25.5 31.5 25.5C31.8978 25.5 32.2794 25.342 32.5607 25.0607C32.842 24.7794 33 24.3978 33 24C33 23.6022 32.842 23.2206 32.5607 22.9393C32.2794 22.658 31.8978 22.5 31.5 22.5ZM35 13C35.2086 13.0001 35.4119 13.0654 35.5816 13.1868C35.7512 13.3082 35.8786 13.4796 35.946 13.677L36.076 14.055C36.376 14.934 37.066 15.625 37.946 15.925L38.323 16.054C38.5202 16.1216 38.6914 16.2491 38.8125 16.4187C38.9337 16.5883 38.9988 16.7915 38.9988 17C38.9988 17.2085 38.9337 17.4117 38.8125 17.5813C38.6914 17.7509 38.5202 17.8784 38.323 17.946L37.945 18.076C37.066 18.376 36.375 19.066 36.075 19.946L35.946 20.323C35.8784 20.5202 35.7509 20.6913 35.5813 20.8125C35.4117 20.9337 35.2085 20.9988 35 20.9988C34.7916 20.9988 34.5883 20.9337 34.4187 20.8125C34.2491 20.6913 34.1216 20.5202 34.054 20.323L33.924 19.945C33.7759 19.5111 33.5303 19.117 33.2062 18.7928C32.882 18.4687 32.4879 18.2231 32.054 18.075L31.677 17.946C31.4798 17.8784 31.3087 17.7509 31.1875 17.5813C31.0663 17.4117 31.0012 17.2085 31.0012 17C31.0012 16.7915 31.0663 16.5883 31.1875 16.4187C31.3087 16.2491 31.4798 16.1216 31.677 16.054L32.055 15.924C32.934 15.624 33.625 14.934 33.925 14.054L34.054 13.677C34.1214 13.4796 34.2488 13.3082 34.4184 13.1868C34.5881 13.0654 34.7914 13.0001 35 13Z" fill="#4B4141" />
              <path d="M24.5117 27.8539C24.5117 27.8539 25.452 30.0078 28.075 30.0078C30.698 30.0078 31.5885 27.832 31.5885 27.832" stroke="#86EFAC" stroke-linecap="round" />
            </g>
            <defs>
              <filter id="filter0_d_1880_2477" x="0" y="0" width="56" height="48" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.09 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1880_2477" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1880_2477" result="shape" />
              </filter>
            </defs>
          </svg>

          <span className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Ask ConvoAI
          </span>
        </button>
      )}

      <PreviewBotPanel
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        botName={botName}
        triggerNode={triggerNode}
        actionNodes={actionNodes}
        templates={templates}
        isRcs={isRcsTrigger}
      />

      <ScrollToTop />
    </div>
  );
};

export default BotBuilderPage;